import { useRef } from 'react';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { RoccoLink } from '@rocco/components/link';
import {
    DetailTileMedia,
    detailTileActionsFor,
} from '~/ui-rocco/pages/detail-shared';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { REVAMP_TILE_SIZES } from '../util/revamp-image-sizes';
import type { RevampArticleTile } from '../type';

interface RevampDiscoverMoreProps {
    tiles: RevampArticleTile[];
}

/**
 * "Discover More Reading" (Figma 2473:15887 + 2456:16466): the giant two-tone
 * heading over rows of four article tiles — portrait image, Gestura serif
 * title and an uppercase date / read-time line. Self-hides when empty.
 */
export const RevampDiscoverMore = ({ tiles }: RevampDiscoverMoreProps) => {
    if (tiles.length === 0) return null;

    return (
        <section className="w-full bg-white px-4.5 md:px-[3rem]">
            <h2 className="m-0 font-sans font-normal text-[#1f1f21] max-md:text-[3rem] max-md:leading-none md:text-[12.5rem] md:leading-[0.9] md:tracking-[-0.042em]">
                {/* The design's #a7a7a8 is 2.4:1 on white — under the 3:1 large
                    text needs. #8c8c8c keeps the two-tone split at 3.4:1. */}
                Discover More <span className="text-[#8c8c8c]">Reading</span>
            </h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-10 max-md:mt-10 md:mt-[11.25rem] md:grid-cols-4 md:gap-x-[1.5rem] md:gap-y-[6.25rem]">
                {tiles.map((tile) => (
                    <DiscoverMoreTile key={tile.id} tile={tile} />
                ))}
            </div>
        </section>
    );
};

/**
 * One rail tile, instrumented like the legacy article rails: an `Impression`
 * per tile (MRC standard via useLogImpression) and a click event. The revamp
 * has a single merged rail, so clicks log the one `ArticleDiscoverMoreClick`
 * name rather than reviving the legacy per-section quartet (BC-4876).
 */
const DiscoverMoreTile = ({ tile }: { tile: RevampArticleTile }) => {
    const tracker = useTracker();
    const tileRef = useRef<HTMLDivElement>(null);

    useLogImpression({
        ref: tileRef,
        data: {
            itemID: tile.id,
            professionalID: tile.professionalID,
            type: 'DiscoverMore',
        },
    });

    const meta = [tile.date, tile.readTime].filter(Boolean).join(', ');
    const actions = detailTileActionsFor({
        id: tile.id,
        link: tile.link,
        title: tile.title,
        images: [{ src: tile.image }],
        pinType: 'ArticlePin',
    });

    return (
        <div className="relative">
            <RoccoLink
                to={tile.link}
                onClick={() => {
                    tracker.log('ArticleDiscoverMoreClick', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                        data: {
                            // ArticleID (legacy rail click parity).
                            ExtraData: tile.id,
                        },
                    });
                }}
            >
                {/* RoccoLink takes no ref, so the impression observes this inner
                wrapper — it spans the full tile content. */}
                <div ref={tileRef} className="flex flex-col">
                    <DetailTileMedia
                        src={tile.image}
                        alt={tile.title}
                        sizes={REVAMP_TILE_SIZES}
                        className="bg-neutral-200 aspect-[437/524]"
                        pinId={tile.id}
                        pinType="ArticlePin"
                        enableActionPanel
                        enquiry={actions.enquiry}
                        share={actions.share}
                        tileInfo={actions.tileInfo}
                    />
                    <p className="m-0 font-gestura font-normal text-[#1f1f21] capitalize max-md:pt-3 max-md:text-base md:pt-[1.875rem] md:pr-[2.4rem] md:text-[1.75rem] md:leading-[2rem] md:tracking-[-0.01em]">
                        {tile.title}
                    </p>
                    {meta && (
                        <p className="m-0 font-sans font-normal tracking-[0.08em] text-[#1f1f21] uppercase max-md:pt-2 max-md:text-xs md:pt-[1.25rem] md:text-[0.75rem] md:leading-[1.125rem]">
                            {meta}
                        </p>
                    )}
                </div>
            </RoccoLink>
            {/* Mobile actions live outside the tile link so a tap on the `•••`
            can never navigate. */}
            <TileMobileActionsSlot
                pinId={tile.id}
                pinType="ArticlePin"
                enableActionPanel
                enquiry={actions.enquiry}
                share={actions.share}
                tileInfo={actions.tileInfo}
            />
        </div>
    );
};
